import { useEffect, useRef, useState } from 'react';
import { AdminSystemStatus, ProcessResourceStatus, ServiceHealthItem, api } from '../lib/api';
import { useI18n } from '../i18n';

function formatBytes(value?: number): string {
  if (value === undefined || value === null) return '—';
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let amount = value;
  let unit = -1;
  do { amount /= 1024; unit += 1; } while (amount >= 1024 && unit < units.length - 1);
  return `${amount.toFixed(amount >= 100 ? 0 : amount >= 10 ? 1 : 2)} ${units[unit]}`;
}

function formatDuration(value: number | undefined, language: string): string {
  if (value === undefined || value === null) return '—';
  const days = Math.floor(value / 86400);
  const hours = Math.floor((value % 86400) / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  if (language === 'ja') return days > 0 ? `${days}日 ${hours}時間` : `${hours}時間 ${minutes}分`;
  if (language === 'en') return days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes}m`;
  return days > 0 ? `${days} 天 ${hours} 小时` : `${hours} 小时 ${minutes} 分`;
}

function ServiceCard({ item, fallbackUptimeSeconds }: { item: ServiceHealthItem; fallbackUptimeSeconds?: number }) {
  const { t, language } = useI18n();
  const online = item.status === 'ONLINE';
  const uptimeSeconds = item.uptimeSeconds ?? fallbackUptimeSeconds;
  const statusLabel = item.status === 'ONLINE'
    ? t('common.online')
    : item.status === 'OFFLINE'
      ? t('common.offline')
      : item.status === 'NOT_CONFIGURED'
        ? t('common.unknown')
        : item.status;

  return (
    <article className={`system-service-card compact-service-card ${item.status.toLowerCase()}`} title={item.message || undefined}>
      <div className="service-card-heading">
        <span className={`status-dot ${online ? 'online' : 'offline'}`} />
        <strong>{item.displayName}</strong>
        <em>{statusLabel}</em>
      </div>
      <div className="service-uptime">
        <span>{t('system.uptime')}</span>
        <strong>{formatDuration(uptimeSeconds, language)}</strong>
      </div>
    </article>
  );
}

function ProcessCard({ title, process }: { title: string; process?: ProcessResourceStatus }) {
  const { language } = useI18n();
  return (
    <article className="metric-panel process-panel">
      <span>{title}</span>
      <strong>{process ? `${formatBytes(process.workingSetBytes)} RAM` : '—'}</strong>
      <small>{process ? `PID ${process.processId} · CPU ${process.cpuPercent ?? '—'}% · ${formatDuration(process.uptimeSeconds, language)}` : '—'}</small>
    </article>
  );
}

export function SystemStatusPage() {
  const { t, locale, language } = useI18n();
  const [status, setStatus] = useState<AdminSystemStatus>();
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const refreshInFlightRef = useRef(false);
  const disposedRef = useRef(false);

  async function load() {
    if (refreshInFlightRef.current || disposedRef.current) return;
    refreshInFlightRef.current = true;
    setRefreshing(true);
    try {
      const next = await api<AdminSystemStatus>('/admin/system-status', {}, {
        timeoutMs: 5_000,
        timeoutMessage: t('system.loadFailed'),
      });
      if (disposedRef.current) return;
      setStatus(next);
      setError('');
    } catch (reason) {
      if (disposedRef.current) return;
      setError(reason instanceof Error ? reason.message : t('system.loadFailed'));
    } finally {
      refreshInFlightRef.current = false;
      if (!disposedRef.current) setRefreshing(false);
    }
  }

  useEffect(() => {
    disposedRef.current = false;
    let timer: number | undefined;
    const tick = async () => {
      await load();
      if (!disposedRef.current) timer = window.setTimeout(() => void tick(), 10_000);
    };
    void tick();
    return () => {
      disposedRef.current = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [language]);

  return (
    <section className="page-section system-status-page">
      <header className="page-header">
        <div><p className="eyebrow">{t('system.eyebrow')}</p><h2>{t('system.title')}</h2></div>
        <button onClick={() => void load()} disabled={refreshing}>{refreshing ? t('common.refreshing') : t('system.refresh')}</button>
      </header>
      {error && <div className="notice error-notice">{error}</div>}

      <section className="system-section">
        <div className="section-title"><h3>{t('system.services')}</h3><span>{status ? t('system.checkedAt', { time: new Date(status.generatedAt).toLocaleTimeString(locale) }) : t('system.checking')}</span></div>
        <div className="system-service-grid">
          {status?.services.map((item) => <ServiceCard key={item.key} item={item} fallbackUptimeSeconds={status.resources.systemUptimeSeconds} />)}
        </div>
      </section>

      <section className="system-section">
        <div className="section-title"><h3>{t('system.resources')}</h3><span>{t('system.resourceHint')}</span></div>
        <div className="metric-grid">
          <article className="metric-panel"><span>{t('system.cpu')}</span><strong>{status?.resources.cpuPercent ?? '—'}%</strong><small>{status?.resources.logicalProcessorCount ?? '—'}</small></article>
          <article className="metric-panel"><span>{t('system.memory')}</span><strong>{formatBytes(status?.resources.memoryUsedBytes)}</strong><small>{formatBytes(status?.resources.memoryFreeBytes)} / {formatBytes(status?.resources.memoryTotalBytes)}</small></article>
          <article className="metric-panel"><span>{t('system.disk', { name: status?.resources.diskName ?? '' })}</span><strong>{formatBytes(status?.resources.diskUsedBytes)}</strong><small>{formatBytes(status?.resources.diskFreeBytes)} / {formatBytes(status?.resources.diskTotalBytes)}</small></article>
          <article className="metric-panel"><span>{t('system.uptime')}</span><strong>{formatDuration(status?.resources.systemUptimeSeconds, language)}</strong><small>Windows Server</small></article>
          <ProcessCard title={t('system.apiProcess')} process={status?.resources.apiProcess} />
          <ProcessCard title={t('system.mediaProcess')} process={status?.resources.mediaServerProcess} />
        </div>
      </section>

      <section className="system-section system-two-column">
        <div className="system-column-panel">
          <div className="section-title"><h3>{t('system.network')}</h3></div>
          <div className="network-grid">
            <article><span>{t('system.upload')}</span><strong>{formatBytes(status?.network.uploadBytesPerSecond)}/s</strong></article>
            <article><span>{t('system.download')}</span><strong>{formatBytes(status?.network.downloadBytesPerSecond)}/s</strong></article>
            <article><span>{t('system.totalSent')}</span><strong>{formatBytes(status?.network.totalSentBytes)}</strong></article>
            <article><span>{t('system.totalReceived')}</span><strong>{formatBytes(status?.network.totalReceivedBytes)}</strong></article>
          </div>
        </div>
        <div className="system-column-panel">
          <div className="section-title"><h3>{t('system.videoBusiness')}</h3><span>{t('system.currentSessions')}</span></div>
          <div className="video-business-grid">
            <article><span>{t('overview.videoSources')}</span><strong>{status?.video.totalVideoSources ?? '—'}</strong><small>{status?.video.onlineVideoSources ?? 0} / {status?.video.offlineVideoSources ?? 0} / {status?.video.disabledVideoSources ?? 0}</small></article>
            <article><span>{t('system.upstreams')}</span><strong>{status?.video.activeUpstreamStreams ?? '—'}</strong><small>{status?.video.startingUpstreamStreams ?? 0} / {status?.video.failedUpstreamStreams ?? 0}</small></article>
            <article><span>{t('system.sessions')}</span><strong>{status?.video.liveViewerSessions ?? '—'}</strong><small>{status?.video.playbackSessions ?? 0}</small></article>
            <article><span>{t('system.users')}</span><strong>{status?.video.activeUsers ?? '—'}</strong><small>{status?.video.enabledUsers ?? 0}</small></article>
          </div>
        </div>
      </section>

      {(status?.video.latestMediaError || status?.video.latestSystemError) && (
        <section className="system-section diagnostics-panel">
          <h3>{t('system.recentErrors')}</h3>
          {status.video.latestMediaError && <p><strong>{t('overview.media')}:</strong> {status.video.latestMediaError}</p>}
          {status.video.latestSystemError && <p><strong>{t('overview.system')}:</strong> {status.video.latestSystemError}</p>}
        </section>
      )}
    </section>
  );
}
